import React, { ChangeEvent, useState } from 'react';
import css from './FragmentQuestionBlock.module.css';
import { notifyErrorCustom } from '@shared/Widgets/ToastNotifications/ToastNotifications';
import { nanoid } from 'nanoid';
import { QuestionTypeEnum } from '@shared/Enums/QuestionTypeEnum';
import RadioInput from '@shared/Ui/CustomInputs/RadioInput/RadioInput';
import { QuestionEntity } from '@entities/Question/Types';
import TextInput from '@shared/Ui/CustomInputs/TextInput/TextInput';

interface FragmentQuestionProps {
    question: QuestionEntity;
    updateQuestionInputValue: (questionUuid: string, value: string) => void;
    updateQuestionSelectValue: (
        questionUuid: string,
        answerUuid: string
    ) => Promise<boolean>;
    isDisabled?: boolean;
}

const FragmentQuestionBlock: React.FC<FragmentQuestionProps> = ({
    question,
    updateQuestionSelectValue,
    updateQuestionInputValue,
    isDisabled = false,
}) => {
    const filteredAnswers = (question.answers || []).filter(a => a?.title);
    const selectedAnswer =
        filteredAnswers.find(answer => answer.selected)?.uuid ?? null;
    const [selectedAnswerUuid, setSelectedAnswerUuid] = useState<string | null>(
        selectedAnswer
    );

    const handleChangeQuestionInputValue =
        () => (e: ChangeEvent<HTMLInputElement>) => {
            let value = e.target.value.replace(',', '.');

            if (!/^\d*\.?\d*$/.test(value)) {
                return;
            }

            if (isNaN(parseFloat(value)) || value === '') {
                updateQuestionInputValue(question.uuid, '0');
                return;
            }

            if (
                question.value === '0' &&
                value.startsWith('0') &&
                value.length > 1
            ) {
                value = value.replace(/^0+/, '');
            }

            updateQuestionInputValue(question.uuid, value);
        };

    const handleChangeQuestionSelectValue =
        (answerUuid: string) => async () => {
            setSelectedAnswerUuid(answerUuid);
            const isDisabled = await updateQuestionSelectValue(
                question.uuid,
                answerUuid
            );
            if (isDisabled) {
                setSelectedAnswerUuid(null);
                notifyErrorCustom('You cannot select this question.');
            }
        };

    const uniqueKey = nanoid();

    switch (question.select) {
        case QuestionTypeEnum.INPUT:
            return (
                <div className={css.question} key="block-modal-key">
                    <div className={css.title} key="block-modal-key-question">
                        {question.question}
                    </div>
                    <TextInput
                        disabled={isDisabled}
                        type="text"
                        value={question.value}
                        onChange={handleChangeQuestionInputValue()}
                        maxLength={20}
                    />
                </div>
            );

        case QuestionTypeEnum.SELECT:
        case QuestionTypeEnum.STYLE: {
            if (!filteredAnswers.length) return null;

            return (
                <div className={css.question} key="block-modal-key">
                    <div className={css.title} key="block-modal-key-question">
                        {question.question}
                    </div>
                    {filteredAnswers.map((answer, idx) => (
                        <div className={css.inputWrapper}>
                            <RadioInput
                                key={idx}
                                name={uniqueKey}
                                disabled={isDisabled}
                                value={answer.value ?? ''}
                                label={answer.title}
                                checked={answer.uuid === selectedAnswerUuid}
                                onChange={handleChangeQuestionSelectValue(
                                    answer.uuid
                                )}
                            />
                        </div>
                    ))}
                </div>
            );
        }

        default:
            return (
                <div className={css.question} key="block-modal-key">
                    Unknown&nbsp;question&nbsp;type:{' '}
                    <strong>{question.select as string}</strong>
                </div>
            );
    }
};

export default FragmentQuestionBlock;
